import type { LucideIcon } from "lucide-react";
import {
  BookOpen,
  Briefcase,
  Cable,
  Heart,
  Landmark,
  LayoutDashboard,
  ListTree,
  Map,
  Menu,
  Network,
  Settings,
  Shield,
  Store,
  Tag,
  Users,
} from "lucide-react";

/** 侧栏菜单项（与后端 menus DTO 同构，后续可改为接口下发） */
export interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
  children?: NavItem[];
  permission?: string;
}

/** 静态菜单配置，后续替换为 menusAtom */
export const navigation: NavItem[] = [
  { name: "仪表盘", href: "/dashboard", icon: LayoutDashboard },
  {
    name: "账号管理",
    href: "/accounts",
    icon: Users,
  },
  { name: "工商实体", href: "/entities", icon: Landmark },
  { name: "代理管理", href: "/agents", icon: Network },
  {
    name: "门店管理",
    href: "/stores",
    icon: Store,
  },
  { name: "员工管理", href: "/employees", icon: Briefcase },
  { name: "会员管理", href: "/members", icon: Heart },
  {
    name: "系统设置",
    href: "/settings",
    icon: Settings,
    children: [
      { name: "标签管理", href: "/settings/tags", icon: Tag },
      { name: "菜单管理", href: "/settings/menus", icon: Menu },
      { name: "角色管理", href: "/settings/roles", icon: Shield },
      { name: "应用映射", href: "/settings/app-mapping", icon: Cable },
      { name: "字典配置", href: "/settings/dictionaries", icon: BookOpen },
      { name: "组织架构", href: "/settings/organizations", icon: ListTree },
      { name: "行政区划", href: "/settings/admin-regions", icon: Map },
    ],
  },
];

/** 面包屑项 */
export interface BreadcrumbItem {
  name: string;
  href?: string;
}

/**
 * 根据 pathname 从菜单树生成面包屑
 * @param pathname 当前路径
 * @param menus 菜单树，默认静态 navigation
 */
export const getBreadcrumbs = (
  pathname: string,
  menus: NavItem[] = navigation,
): BreadcrumbItem[] => {
  for (const item of menus) {
    if (item.children?.length) {
      const child = item.children.find((c) => c.href === pathname);
      if (child) {
        return [{ name: item.name }, { name: child.name, href: child.href }];
      }
    } else if (pathname === item.href) {
      return [{ name: item.name, href: item.href }];
    }
  }
  return [];
};

/**
 * 取菜单第一项的落地路径（登录后 / 的 index 重定向目标）
 * @param menus 菜单树，默认 navigation
 */
export const getDefaultHomePath = (menus: NavItem[] = navigation): string => {
  const first = menus[0];
  if (!first) {
    return "/";
  }
  if (first.children?.length) {
    return first.children[0].href;
  }
  return first.href;
};

/**
 * 收集所有叶子路由（用于注册 React Router）
 * @param menus 菜单树
 */
export const collectLeafRoutes = (
  menus: NavItem[] = navigation,
): { href: string; title: string }[] => {
  const routes: { href: string; title: string }[] = [];
  for (const item of menus) {
    if (item.children?.length) {
      for (const child of item.children) {
        routes.push({ href: child.href, title: child.name });
      }
    } else {
      routes.push({ href: item.href, title: item.name });
    }
  }
  return routes;
};

/** 将菜单 href 转为布局下的相对 path（首页为 ''） */
export const toRouteSegment = (href: string): string => {
  if (href === "/") {
    return "";
  }
  return href.replace(/^\//, "");
};

/**
 * 有子菜单的父级路径 → 重定向到第一个子菜单
 * @param menus 菜单树
 */
export const collectParentRedirects = (
  menus: NavItem[] = navigation,
): { parentSegment: string; childHref: string }[] => {
  const list: { parentSegment: string; childHref: string }[] = [];
  for (const item of menus) {
    if (item.children?.length) {
      list.push({
        parentSegment: toRouteSegment(item.href),
        childHref: item.children[0].href,
      });
    }
  }
  return list;
};
